import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface AuthUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  userType: string;
  roles: string[];
  /** Resolved RBAC set (module → level) from the profile — UX gating only. */
  permissions?: Record<string, string>;
  /** The one site this person works at; undefined = headquarters (every site).
   *  Pins the site switcher: somebody who belongs to one farm is not offered
   *  the others. The gateways enforce it regardless (`infra_admission`). */
  homeSiteId?: string;
}

/** Token expiries as the backend reports them (RFC3339). Optional because a
 *  session restored from the v1 store predates them — those simply fall back to
 *  reactive refresh instead of the proactive schedule. */
export interface SessionExpiry {
  accessExp?: string;
  refreshExp?: string;
}

/** One signed-in account. R-303: the console holds several at once and swaps
 *  which one its requests carry. */
export interface AuthAccount extends SessionExpiry {
  accessToken: string;
  refreshToken: string;
  user: AuthUser;
}

interface AuthState {
  /** Every signed-in account, keyed by user id. */
  accounts: Record<string, AuthAccount>;
  /** Which one requests currently carry. */
  activeId: string | null;
  /** True while the operator is signing IN an ADDITIONAL account — the Router
   *  shows the login screen even though a session is already active. Never
   *  persisted: a reload must not strand somebody in add-account mode. */
  addingAccount: boolean;

  // ── Projection of the active account ────────────────────────────────────────
  // Derived, never edited directly. It exists so the ~17 places that select
  // `accessToken` / `user` / `isAuthenticated` did not all have to change when
  // multi-account landed — the single-account read model is still true, it just
  // describes whichever account is active.
  accessToken: string | null;
  refreshToken: string | null;
  user: AuthUser | null;
  isAuthenticated: boolean;
}

interface AuthActions {
  /** Sign in an account (or replace its tokens) and make it the active one. */
  setSession: (access: string, refresh: string, user: AuthUser, exp?: SessionExpiry) => void;
  /** Rotate the ACTIVE account's tokens. */
  setTokens: (access: string, refresh: string, exp?: SessionExpiry) => void;
  /** Rotate a SPECIFIC account's tokens — the background refresher and the
   *  switch-time validation both act on an account that may not be active. */
  setTokensFor: (userId: string, access: string, refresh: string, exp?: SessionExpiry) => void;
  /** Refresh the stored profile of the active account (boot revalidation). */
  setUser: (user: AuthUser) => void;
  /** Make an already-signed-in account the active one. No-op if unknown. */
  switchTo: (userId: string) => void;
  /** Drop one account. If it was active, the next remaining one takes over —
   *  signing out of A while B is signed in lands on B, not on the login screen. */
  removeAccount: (userId: string) => void;
  /** Sign out of the ACTIVE account (see `removeAccount` for what follows). */
  clearSession: () => void;
  beginAddAccount: () => void;
  cancelAddAccount: () => void;
}

type Projection = Pick<AuthState, 'accessToken' | 'refreshToken' | 'user' | 'isAuthenticated'>;

/** Exactly what `partialize` writes to storage — `migrate` must return this shape. */
type PersistedAuth = Pick<AuthState, 'accounts' | 'activeId'>;

/** The one place the active account becomes the flat read model. */
function project(accounts: Record<string, AuthAccount>, activeId: string | null): Projection {
  const active = activeId ? accounts[activeId] : undefined;
  return {
    accessToken: active?.accessToken ?? null,
    refreshToken: active?.refreshToken ?? null,
    user: active?.user ?? null,
    isAuthenticated: active != null,
  };
}

/** `exactOptionalPropertyTypes` forbids assigning an explicit `undefined`, so an
 *  absent expiry must be an absent KEY. */
function withExpiry(exp: SessionExpiry | undefined): SessionExpiry {
  return {
    ...(exp?.accessExp ? { accessExp: exp.accessExp } : {}),
    ...(exp?.refreshExp ? { refreshExp: exp.refreshExp } : {}),
  };
}

const initial: Pick<AuthState, 'accounts' | 'activeId' | 'addingAccount'> = {
  accounts: {},
  activeId: null,
  addingAccount: false,
};

export const useAuthStore = create<AuthState & AuthActions>()(
  persist(
    (set) => ({
      ...initial,
      ...project({}, null),

      setSession: (access, refresh, user, exp) =>
        set((s) => {
          const accounts: Record<string, AuthAccount> = {
            ...s.accounts,
            [user.id]: { accessToken: access, refreshToken: refresh, user, ...withExpiry(exp) },
          };
          return { accounts, activeId: user.id, addingAccount: false, ...project(accounts, user.id) };
        }),

      setTokens: (access, refresh, exp) =>
        set((s) => {
          if (!s.activeId) return s;
          return applyTokens(s, s.activeId, access, refresh, exp);
        }),

      setTokensFor: (userId, access, refresh, exp) =>
        set((s) => applyTokens(s, userId, access, refresh, exp)),

      setUser: (user) =>
        set((s) => {
          const existing = s.activeId ? s.accounts[s.activeId] : undefined;
          if (!s.activeId || !existing) return s;
          const accounts = { ...s.accounts, [s.activeId]: { ...existing, user } };
          return { accounts, ...project(accounts, s.activeId) };
        }),

      switchTo: (userId) =>
        set((s) => (s.accounts[userId] ? { activeId: userId, ...project(s.accounts, userId) } : s)),

      removeAccount: (userId) =>
        set((s) => {
          if (!s.accounts[userId]) return s;
          const accounts = { ...s.accounts };
          delete accounts[userId];
          const activeId = s.activeId === userId ? (Object.keys(accounts)[0] ?? null) : s.activeId;
          return { accounts, activeId, ...project(accounts, activeId) };
        }),

      clearSession: () =>
        set((s) => {
          if (!s.activeId) return s;
          const accounts = { ...s.accounts };
          delete accounts[s.activeId];
          const activeId = Object.keys(accounts)[0] ?? null;
          return { accounts, activeId, addingAccount: false, ...project(accounts, activeId) };
        }),

      beginAddAccount: () => set({ addingAccount: true }),
      cancelAddAccount: () => set({ addingAccount: false }),
    }),
    {
      name: 'xantory:auth',
      version: 2,
      // Only the accounts are durable. The projection is recomputed in `merge`
      // rather than stored, so it can never be persisted out of step with them.
      partialize: (s) => ({ accounts: s.accounts, activeId: s.activeId }),
      migrate: migrateAuth,
      merge: (persisted, current) => {
        const p = persisted as Partial<PersistedAuth> | undefined;
        const { accounts, activeId } = reconcilePersisted(p);
        return { ...current, accounts, activeId, addingAccount: false, ...project(accounts, activeId) };
      },
    },
  ),
);

/** The storage key the auth store persists under — what the cross-tab sync listens for. */
export const AUTH_STORAGE_KEY = 'xantory:auth';

/**
 * **Tabs share ONE stored session but not one memory (R-366).** Each tab hydrates
 * from storage once at load and then keeps its own copy. When tab A rotates the
 * tokens, tab B's copy is stale: its next refresh presents a pair the backend has
 * already replaced, is refused, and — before R-366 — B signed the whole browser out
 * of a session that was perfectly alive in A. Two tabs on the same schedule made this
 * a race that fired every fifteen minutes.
 *
 * The `storage` event fires in every OTHER tab when one writes the key, so re-reading
 * the persisted state there keeps every tab on the pair the session actually runs on.
 * Idempotent and cheap; a tab that wrote the change receives no event for it.
 */
export function syncAuthAcrossTabs(): () => void {
  if (typeof window === 'undefined') return () => {};
  const onStorage = (e: StorageEvent) => {
    if (e.key !== null && e.key !== AUTH_STORAGE_KEY) return;
    void useAuthStore.persist.rehydrate();
  };
  window.addEventListener('storage', onStorage);
  return () => window.removeEventListener('storage', onStorage);
}

/** Shared by `setTokens` / `setTokensFor` — rotation must never touch the profile. */
function applyTokens(
  s: AuthState,
  userId: string,
  access: string,
  refresh: string,
  exp: SessionExpiry | undefined,
): Partial<AuthState> {
  const existing = s.accounts[userId];
  if (!existing) return s;
  const accounts: Record<string, AuthAccount> = {
    ...s.accounts,
    [userId]: { ...existing, accessToken: access, refreshToken: refresh, ...withExpiry(exp) },
  };
  return { accounts, ...project(accounts, s.activeId) };
}

/**
 * v1 → v2 storage migration (R-303). Exported for tests: **get this wrong and every
 * signed-in operator lands on the login screen the moment the deploy ships**, which
 * is the single most disruptive way this change could fail.
 *
 * v1 held one flat session (`accessToken` / `refreshToken` / `user`); v2 holds a map
 * keyed by user id. A v1 session becomes account #1 and stays active.
 */
export function migrateAuth(persisted: unknown, version: number): PersistedAuth {
  if (version >= 2) return (persisted as PersistedAuth | null) ?? { accounts: {}, activeId: null };
  const v1 = persisted as Partial<{
    accessToken: string | null;
    refreshToken: string | null;
    user: AuthUser | null;
  }> | null;
  if (v1?.accessToken && v1.refreshToken && v1.user?.id) {
    return {
      accounts: {
        [v1.user.id]: { accessToken: v1.accessToken, refreshToken: v1.refreshToken, user: v1.user },
      },
      activeId: v1.user.id,
    };
  }
  return { accounts: {}, activeId: null };
}

/**
 * Resolve what was read from storage into a coherent pair. Exported for tests.
 *
 * Self-heals a dangling pointer: an `activeId` naming an account that is no longer
 * stored would otherwise project as "authenticated" with **no token** — every request
 * 401ing with nothing to refresh, which presents as a console that is signed in and
 * totally broken rather than one that is signed out.
 */
export function reconcilePersisted(p: Partial<PersistedAuth> | undefined): PersistedAuth {
  const accounts = p?.accounts ?? {};
  const activeId = p?.activeId && accounts[p.activeId] ? p.activeId : (Object.keys(accounts)[0] ?? null);
  return { accounts, activeId };
}

/** The active-account projection. Exported for tests. */
export const projectActive = project;
export type { PersistedAuth };
